import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DownloadingOutlinedIcon from '@mui/icons-material/DownloadingOutlined'
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../../auth/AuthContext'
import config from '../../../config'
import type { PaymentCreateCheckoutResponse, PlanDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { SUBSCRIPTION_PATH } from '../../subscription/common/subscription-common'
import { errorMessage, money, PERIODICIDAD_LABELS, planFeature, PRICE_SUFFIX } from '../common/plan-common'
import type { PlanFeature } from '../common/plan-common'

export type PlanPeriod = 'mensual' | 'anual'

export interface PlanCard {
    id: string
    stripeId: string | null
    name: string
    price: string
    priceSuffix: string
    periodLabel: string
    recommended: boolean
    features: PlanFeature[]
}

const LOAD_ERROR = 'No se pudieron cargar los planes.'
const CHECKOUT_ERROR = 'No se pudo iniciar el pago. Inténtalo de nuevo.'

export const PLAN_PERIODS: Array<{ id: PlanPeriod; label: string }> = [
    { id: 'mensual', label: 'Mensual' },
    { id: 'anual', label: 'Anual' },
]

export const INCLUDED_MODULES = [
    'Módulos de Pólizas',
    'Módulos de Cobranza',
    'Módulos de Renovaciones',
    'Módulos de Siniestros',
    'Módulos de Prospectos',
    'Módulos de Calendario',
    'Módulos de Reportes',
    'Seguimiento de procesos',
    'Envío de Correos',
    'Integración de Pólizas',
    'Captura Manual',
    'Importación de PDFs',
    'Integración Automática',
    'Integración con Todoist*',
]

export const REASONS = [
    { id: 'sync', Icon: DownloadingOutlinedIcon, tone: 'bg-tone-blue text-tone-blue-ink', title: 'Sincronización con las aseguradoras', text: 'Utilizamos complejos mecanismos de integración con las principales aseguradoras para sincronizar tus pólizas.' },
    { id: 'pdf', Icon: DescriptionOutlinedIcon, tone: 'bg-tone-purple text-tone-purple-ink', title: 'Procesamiento de carátulas PDF', text: 'Extraemos la información de las carátulas que usas todos los días; contamos con más de 60 formatos de las principales aseguradoras.' },
    { id: 'cobranza', Icon: MonetizationOnOutlinedIcon, tone: 'bg-tone-teal text-tone-teal-ink', title: 'Gestión de cobranza y renovaciones', text: 'Herramientas para dar seguimiento a estos procesos con ordenamientos automáticos de tu trabajo.' },
    { id: 'metas', Icon: BarChartOutlinedIcon, tone: 'bg-tone-orange text-tone-orange-ink', title: 'Seguimiento de metas', text: 'Configura y supervisa tus metas de ventas mensuales o anuales con indicadores que miden tu progreso día a día.' },
]

export const CUSTOM_PLAN_MAILTO = `mailto:${config.contacts.emailContacto}?Subject=Información%20sobre%20planes%20personalizados`

const toCard = (plan: PlanDTO): PlanCard => ({
    id: String(plan.productId ?? plan.stripeId ?? plan.nombre),
    stripeId: plan.stripeId,
    name: plan.nombre || 'Plan',
    price: money(plan.precio),
    priceSuffix: plan.periodicidad ? PRICE_SUFFIX[plan.periodicidad] || '' : '',
    periodLabel: plan.periodicidad === 'anual' ? 'Renta anual' : plan.periodicidad === 'mensual' ? 'Renta mensual' : (plan.periodicidad && PERIODICIDAD_LABELS[plan.periodicidad]) || '',
    recommended: plan.recomendado,
    features: (plan.items || []).map(planFeature),
})

export const usePlanListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { user } = useAuth()
    const backUrl: string | undefined = (location.state as { backUrl?: string } | null)?.backUrl

    const [period, setPeriod] = useState<PlanPeriod>('mensual')
    const [plans, setPlans] = useState<PlanDTO[]>([])
    const [loading, setLoading] = useState(true)
    const [checkoutId, setCheckoutId] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    const activePeriodicidad = user?.suscripcion?.plan?.periodicidad
    const hasPaidPlan = Boolean(activePeriodicidad && activePeriodicidad !== 'prueba')

    useEffect(() => {
        if (hasPaidPlan) history.replace(SUBSCRIPTION_PATH)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPaidPlan])

    useEffect(() => {
        const resetCheckout = (event: PageTransitionEvent) => {
            if (event.persisted) setCheckoutId(null)
        }
        window.addEventListener('pageshow', resetCheckout)
        return () => window.removeEventListener('pageshow', resetCheckout)
    }, [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response: WrapperResponse<PlanDTO[]> | null = await (await APIInvoke.invokeGET(`/planes?periodicidad=${period.toUpperCase()}`)).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                setPlans([...(response.body || [])].sort((left, right) => left.orden - right.orden))
            } catch (loadError) {
                console.error(loadError)
                if (active) {
                    setPlans([])
                    setError(errorMessage(loadError, LOAD_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [period])

    const selectPlan = async (plan: PlanCard) => {
        if (!plan.stripeId || checkoutId) return
        try {
            setCheckoutId(plan.id)
            setError(null)
            const response: WrapperResponse<PaymentCreateCheckoutResponse> = await (await APIInvoke.invokePOST(`/payments/stripe/create-checkout?priceId=${encodeURIComponent(plan.stripeId)}`)).json()
            if (!response.ok || !response.body?.redirectURL) throw new Error(response.message || CHECKOUT_ERROR)
            window.location.assign(response.body.redirectURL)
        } catch (checkoutError) {
            console.error(checkoutError)
            setError(errorMessage(checkoutError, CHECKOUT_ERROR))
            setCheckoutId(null)
        }
    }

    return {
        period,
        loading,
        error,
        checkoutId,
        plans: plans.map(toCard),
        backTo: backUrl,
        changePeriod: (value: PlanPeriod | null) => { if (value) setPeriod(value) },
        selectPlan,
    }
}
